import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listVendorAccess, grantVendorAccess, revokeVendorAccess, vendorErrorInfo, getHqCatalog } from '../../api/client';
import { readFromResponse, admissionFrom, ADMISSION_PENDING, DirectoryRead, Admission } from './vendorSources';
import { createVendorAttempts, vendorKey, VendorApi, VendorOp, SendResult, AttemptRecord } from './vendorGrantAttempt';
import { useAttemptStore } from './useAttemptStore';
import * as durable from './unresolvedChange';

export const vendorApi: VendorApi = { grant: grantVendorAccess, revoke: revokeVendorAccess };
export const currentVendorAttempt = (employeeId: string): AttemptRecord | null => (durable.getUnresolved(vendorKey(employeeId)) as AttemptRecord | null);

/** Reads the existing Vendor service list. A failed, refused or unreadable read is `unknown` - never "no grant". */
export function useVendorDirectory(enabled: boolean): { read: DirectoryRead; reload: () => Promise<void> } {
  const [read, setRead] = useState<DirectoryRead>(enabled ? { status: 'loading' } : { status: 'unknown', message: 'Only a Company Admin can read named Vendors access, so it is not shown to you.' });
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const reload = useCallback(async () => {
    if (!enabled) { setRead({ status: 'unknown', message: 'Only a Company Admin can read named Vendors access, so it is not shown to you.' }); return; }
    try {
      const r = await listVendorAccess();
      if (live.current) setRead(readFromResponse(r, vendorErrorInfo));
    } catch { if (live.current) setRead({ status: 'unknown', message: 'Named Vendors access could not be read (the server could not be reached), so it is unknown.' }); }
  }, [enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { read, reload };
}

export type Notice = { kind: 'ok' | 'error' | 'unknown'; text: string } | null;
/**
 * The grant / remove / resend actions with their durable attempt, shared by the person section and the inventory.
 * ADMISSION: every action first asks the HQ catalog FRESH (authenticated viewer is a Company Admin AND cutover/write readiness is affirmatively open). Closed, unknown, unreadable or
 * unauthorized = nothing is recorded and nothing is sent. `admission` is the last answer, for the screens to show read-only.
 */
export function useVendorActions(opts: { reload: () => Promise<void>; onChanged?: () => void | Promise<void> }) {
  useAttemptStore();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [admission, setAdmission] = useState<Admission>(ADMISSION_PENDING);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const ask = useCallback(async (): Promise<Admission> => {
    let a: Admission;
    try { a = admissionFrom(await getHqCatalog()); } catch { a = admissionFrom(null); }
    if (live.current) setAdmission(a);
    return a;
  }, []);
  useEffect(() => { ask(); }, [ask]);
  const attempts = useMemo(() => createVendorAttempts(durable as any, vendorApi, ask), [ask]);
  const finish = async (res: SendResult, what: string) => {
    if (res.kind === 'not-started') setNotice({ kind: 'error', text: res.reason === 'not-admitted' ? `${res.detail} Nothing was sent.` : durable.notStartedText(res.failure) });
    else if (res.kind === 'applied') { setNotice({ kind: 'ok', text: res.replayed ? `This change had already been recorded (${what}).` : `Saved: ${what}. This is saved on its own, separately from the permissions Save.` }); await opts.reload(); await opts.onChanged?.(); }
    else if (res.kind === 'refused') { setNotice({ kind: 'error', text: `${what} was refused: ${res.response ? vendorErrorInfo(res.response).message : 'refused.'} Nothing was changed.` }); await opts.reload(); }
    else setNotice({ kind: 'unknown', text: `It is not known whether ${what} was saved. It stays unresolved with the same request: use "Send again". Looking at the current list does not settle it.` });
  };
  const run = async (fn: () => Promise<SendResult>, what: string) => { if (busy) return; setBusy(true); setNotice(null); try { await finish(await fn(), what); } finally { setBusy(false); } };
  return {
    busy, notice, setNotice, admission,
    attemptFor: (employeeId: string): AttemptRecord | null => currentVendorAttempt(employeeId),
    grant: (employeeId: string, level: 'view' | 'maintain' | 'administer', fromLevel: string | null, who: string) =>
      run(() => attempts.start(crypto.randomUUID(), { op: 'grant', employeeId, level, fromLevel } as VendorOp), `named Vendors access for ${who}`),
    revoke: (employeeId: string, grantId: string, level: string, who: string) =>
      run(() => attempts.start(crypto.randomUUID(), { op: 'revoke', employeeId, grantId, level } as VendorOp), `removal of named Vendors access for ${who}`),
    resend: (rec: AttemptRecord, who: string) => run(() => attempts.resend(rec), rec.payload.op === 'grant' ? `named Vendors access for ${who}` : `removal of named Vendors access for ${who}`),
  };
}
